import { memo } from 'react';
import { Alert, Box, Button, SpaceBetween, TextContent } from '@amzn/awsui-components-core';

import { useAuthorQualityCheckStageViewModel } from './AuthorQualityCheckStatePanelViewModel';
import { SaveErrorAlert } from './SaveErrorAlert';
import PanelSection from './PanelSection';
import ScanFindingsTable from './ScanFindingsTable';
import { PanelDivider } from './PanelDivider';
import { ThumbsFeedback } from '../v2-common/rum-feedback';
import { FailedQualityCheckConfirmationModal } from './FailedQualityCheckConfirmationModal';
import { ScanTimeoutAlert } from './ScanTimeoutAlert';
import { ScanFailedAlert } from './ScanFailedAlert';

const QUALITY_CHECK_EVENT_TYPE = 'quality_check';

export const AuthorQualityCheckStatePanel = memo(function AuthorQualityCheckStatePanel() {
  const vm = useAuthorQualityCheckStageViewModel();

  if (vm.isScanning) {
    return (
      <SpaceBetween size="m" direction="vertical" alignItems="center">
        <Box color="text-body-secondary">Scanning your content for quality issues…</Box>
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m" direction="vertical">
      {vm.isPublisherReviewConfirmationVisible && (
        <FailedQualityCheckConfirmationModal
          onDismiss={vm.dismissPublisherReviewConfirmation}
          onConfirm={vm.confirmPublisherReview}
        />
      )}

      <ScanFindingsTable
        scannerFindings={vm.scannerFindings}
        isLoading={vm.isLoadingFindings}
        onRescan={vm.startQualityCheck}
        isRescanDisabled={vm.isRescanDisabled}
        isScanCancelled={vm.isScanCancelled}
      />

      {vm.scanWarningMessage && (
        <Alert type="warning" header="Content flagged">
          {vm.scanWarningMessage}
        </Alert>
      )}

      {vm.isScanTimedOut && <ScanTimeoutAlert />}

      {vm.isScanFailed && (
        <ScanFailedAlert onRescan={vm.startQualityCheck} isRescanDisabled={vm.isRescanDisabled}>
          The quality check scan could not be completed. Rescan to try again, or contact APG support
          if the problem persists.
        </ScanFailedAlert>
      )}

      {vm.scanFeedbackComposeEvent && (
        <ThumbsFeedback
          eventType={QUALITY_CHECK_EVENT_TYPE}
          label="Helpful scan results?"
          composeEvent={vm.scanFeedbackComposeEvent}
        />
      )}

      {vm.requirementsToProceed.length > 0 && <PanelDivider />}

      {vm.requirementsToProceed.length > 0 && (
        <PanelSection title="Required to proceed">
          <TextContent>
            <ul>
              {vm.requirementsToProceed.map((requirement) => (
                <li key={requirement}>{requirement}</li>
              ))}
            </ul>
          </TextContent>
        </PanelSection>
      )}

      {vm.errorMessage && (
        <SaveErrorAlert
          errorMessage={vm.errorMessage}
          validationIssues={vm.validationIssues}
          onDismiss={vm.dismissError}
        />
      )}

      <SpaceBetween size="xs" direction="vertical">
        <Button
          variant="normal"
          fullWidth
          disabled={vm.isAllResolved || vm.isBusy || vm.isScanCancelled}
          loading={vm.isAcceptAllLoading}
          onClick={vm.acceptAllChanges}
        >
          Accept all changes
        </Button>
        {vm.isScanCancelled ? (
          <Button
            variant="primary"
            fullWidth
            disabled={vm.isRescanDisabled}
            onClick={vm.startQualityCheck}
          >
            Scan again
          </Button>
        ) : (
          <Button
            variant="primary"
            fullWidth
            disabled={!vm.isAllResolved || vm.isBusy || vm.isScanTimedOut}
            onClick={vm.startPublisherReview}
          >
            Submit for publisher review
          </Button>
        )}
        <Button
          variant="link"
          fullWidth
          disabled={vm.isBusy}
          loading={vm.isSaveLoading}
          onClick={vm.saveAndClose}
        >
          Save and close
        </Button>
      </SpaceBetween>
    </SpaceBetween>
  );
});
